import { Box, Typography } from '@mui/material'
import ChatIcon from '@mui/icons-material/Chat'
import CodeIcon from '@mui/icons-material/Code'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { Icon } from '@/components/common'
import { ProcessStep } from '@/types'

/**
 * Process step card props
 */
export interface ProcessStepCardProps {
  /** Process step data */
  step: ProcessStep
  /** Whether to show arrow (not for last step) */
  showArrow?: boolean
}

/**
 * Icon mapping for process steps
 */
const processIcons = {
  Chat: ChatIcon,
  Code: CodeIcon,
  Rocket: RocketLaunchIcon,
}

/**
 * ProcessStepCard Component
 * Displays a single process step
 */
export const ProcessStepCard = ({ step, showArrow = false }: ProcessStepCardProps) => {
  const IconComponent = processIcons[step.icon as keyof typeof processIcons] || ChatIcon

  return (
    <Box
      sx={{
        position: 'relative',
        textAlign: 'center',
      }}
    >
      {/* Step Number Circle */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          fontSize: '2rem',
          fontWeight: 700,
        }}
      >
        {step.stepNumber}
      </Box>

      {/* Icon */}
      <Icon
        icon={IconComponent}
        size="large"
        color="primary"
        sx={{
          fontSize: '3rem',
          mb: 2,
        }}
      />

      {/* Title */}
      <Typography
        variant="h5"
        component="h3"
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        {step.title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.6,
        }}
      >
        {step.description}
      </Typography>

      {/* Arrow for desktop */}
      {showArrow && (
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 40,
            right: -60,
            fontSize: '2rem',
            color: 'primary.main',
            fontWeight: 700,
          }}
        >
          →
        </Box>
      )}
    </Box>
  )
}
